import { useEffect, useRef, useState } from "react";
import { crear, eliminar as eliminarFila, funcion, rpc } from "../../services/datos";
import { botonPequeno, botonPrimario, campo, etiqueta } from "../../shared/estilos";
import { confirmar } from "../../shared/avisos";

const ENLACE_MUSICA = /^https?:\/\/(www\.|m\.|music\.)?(youtube\.com|youtu\.be|open\.spotify\.com|spotify\.link)\//i;
const vacio = { cancion: "", artista: "", enlace: "" };

export const plataformaDe = (enlace) =>
  /spotify/i.test(enlace || "") ? "Spotify" : /youtu/i.test(enlace || "") ? "YouTube" : null;

// Mueve el elemento de la posición `desde` a la posición `hasta`.
export function mover(lista, desde, hasta) {
  const copia = [...lista];
  const [elemento] = copia.splice(desde, 1);
  copia.splice(hasta, 0, elemento);
  return copia;
}

// Alabanza del programa: se pega el enlace de YouTube o Spotify y se llenan solos
// el nombre y el autor; las canciones se ordenan arrastrándolas (o con las flechas).
function Alabanza({ canciones, asegurarProgramacion, onCambio, avisar }) {
  const [form, setForm] = useState(vacio);
  const [buscando, setBuscando] = useState(false);
  const [encontrado, setEncontrado] = useState(null);
  const [error, setError] = useState("");
  const [guardando, setGuardando] = useState(false);
  const [arrastrando, setArrastrando] = useState(null);
  const [lista, setLista] = useState(canciones);
  const consulta = useRef(0);

  useEffect(() => { setLista(canciones); }, [canciones]);

  // Al pegar un enlace válido se buscan sus datos (con una pequeña espera mientras se escribe).
  useEffect(() => {
    const enlace = form.enlace.trim();
    if (!ENLACE_MUSICA.test(enlace)) {
      setEncontrado(null);
      return undefined;
    }
    const numero = ++consulta.current;
    const espera = setTimeout(async () => {
      setBuscando(true);
      setError("");
      try {
        const datos = await funcion("datos-cancion", { url: enlace });
        if (numero !== consulta.current) return;
        setEncontrado(datos);
        setForm((actual) => ({ ...actual, cancion: datos.cancion || actual.cancion, artista: datos.artista || actual.artista }));
      } catch (e) {
        if (numero === consulta.current) setError(e.message);
      } finally {
        if (numero === consulta.current) setBuscando(false);
      }
    }, 400);
    return () => clearTimeout(espera);
  }, [form.enlace]);

  const agregar = async () => {
    if (!form.cancion.trim()) {
      setError("Pega un enlace o escribe el nombre de la canción.");
      return;
    }
    setGuardando(true);
    setError("");
    try {
      const programacion = await asegurarProgramacion();
      const nueva = await crear("canciones_programacion", {
        programacion_id: programacion.id,
        cancion: form.cancion.trim(),
        artista: form.artista.trim(),
        enlace: form.enlace.trim(),
        orden: lista.length + 1,
      });
      onCambio([...lista, nueva]);
      setForm(vacio);
      setEncontrado(null);
      avisar(`“${nueva.cancion}” agregada al final. Arrástrala para cambiar su lugar.`);
    } catch (e) {
      setError(e.message || "No se pudo agregar la canción.");
    } finally {
      setGuardando(false);
    }
  };

  const eliminar = async (item) => {
    if (!(await confirmar(`¿Quitar “${item.cancion}” del programa?`, { aceptar: "Quitar" }))) return;
    try {
      await eliminarFila("canciones_programacion", item.id);
      const restantes = lista.filter((c) => c.id !== item.id);
      onCambio(restantes);
      if (restantes.length) await guardarOrden(restantes, restantes);
    } catch (e) {
      avisar(e.message || "No se pudo quitar la canción.");
    }
  };

  const guardarOrden = async (nuevaLista, anterior) => {
    const ordenadas = nuevaLista.map((c, i) => ({ ...c, orden: i + 1 }));
    setLista(ordenadas);
    try {
      await rpc("reordenar_canciones", { p_programacion: ordenadas[0].programacion_id, p_ids: ordenadas.map((c) => c.id) });
      onCambio(ordenadas);
    } catch (e) {
      setLista(anterior);
      avisar(e.message || "No se pudo guardar el nuevo orden.");
    }
  };

  const moverA = (desde, hasta) => {
    if (hasta < 0 || hasta >= lista.length || desde === hasta) return;
    guardarOrden(mover(lista, desde, hasta), lista);
  };

  return (
    <section className="panel p-5">
      <h3 className="text-xl font-black uppercase tracking-tight text-white">Canciones en orden</h3>

      <div className="mt-5 space-y-3 border-b border-gray-800 pb-5">
        <div>
          <label htmlFor="cancion-enlace" className={etiqueta}>Enlace de Spotify o YouTube</label>
          <input id="cancion-enlace" type="url" className={campo} value={form.enlace} placeholder="Pega aquí el enlace de la canción"
            onChange={(e) => setForm({ ...form, enlace: e.target.value })} />
          <p className="mt-1 text-xs text-gray-500" aria-live="polite">
            {buscando ? "Buscando la canción..." : encontrado ? `Encontrada en ${encontrado.plataforma}. Revisa los datos y agrégala.` : "El nombre y el autor se llenan solos."}
          </p>
        </div>
        <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
          <div>
            <label htmlFor="cancion-nombre" className={etiqueta}>Canción</label>
            <input id="cancion-nombre" className={campo} value={form.cancion} onChange={(e) => setForm({ ...form, cancion: e.target.value })} />
          </div>
          <div>
            <label htmlFor="cancion-artista" className={etiqueta}>Autor o intérprete</label>
            <input id="cancion-artista" className={campo} value={form.artista} onChange={(e) => setForm({ ...form, artista: e.target.value })} />
          </div>
        </div>
        {error && <p role="alert" className="text-sm text-red-300">{error}</p>}
        <button onClick={agregar} disabled={guardando || buscando} className={`${botonPrimario} w-full`}>
          {guardando ? "Agregando..." : "Agregar canción"}
        </button>
      </div>

      {lista.length === 0 ? (
        <p className="mt-5 text-sm text-gray-400">Aún no hay canciones. Pega el enlace de la primera.</p>
      ) : (
        <>
          <p className="mt-5 text-xs text-gray-500">Arrastra las canciones (o usa las flechas) para ordenarlas como se van a cantar.</p>
          <ol className="mt-3 space-y-2">
            {lista.map((item, i) => (
              <li key={item.id} draggable
                onDragStart={(e) => { setArrastrando(i); e.dataTransfer.effectAllowed = "move"; }}
                onDragOver={(e) => e.preventDefault()}
                onDrop={(e) => { e.preventDefault(); if (arrastrando !== null) moverA(arrastrando, i); setArrastrando(null); }}
                onDragEnd={() => setArrastrando(null)}
                className={`flex cursor-grab items-center gap-3 rounded-xl border bg-black p-3 transition active:cursor-grabbing ${arrastrando === i ? "border-white/60 opacity-50" : "border-gray-700"}`}>
                <span aria-hidden="true" className="select-none text-gray-600">⋮⋮</span>
                <span className="flex h-7 w-7 shrink-0 items-center justify-center rounded-full bg-white text-sm font-bold text-black">{i + 1}</span>
                <div className="min-w-0 flex-1">
                  <p className="truncate font-semibold text-white">{item.cancion}</p>
                  <p className="truncate text-xs text-gray-400">
                    {item.artista || "Autor no indicado"}
                    {item.enlace && (
                      <a href={item.enlace} target="_blank" rel="noreferrer" className="ml-2 underline hover:text-white">
                        Escuchar en {plataformaDe(item.enlace) || "enlace"}
                      </a>
                    )}
                  </p>
                </div>
                <div className="flex shrink-0 items-center gap-1">
                  <button onClick={() => moverA(i, i - 1)} disabled={i === 0} aria-label={`Subir ${item.cancion}`} className={botonPequeno}>↑</button>
                  <button onClick={() => moverA(i, i + 1)} disabled={i === lista.length - 1} aria-label={`Bajar ${item.cancion}`} className={botonPequeno}>↓</button>
                  <button onClick={() => eliminar(item)} aria-label={`Quitar ${item.cancion}`} className="px-2 text-sm text-red-300 hover:text-red-200">Quitar</button>
                </div>
              </li>
            ))}
          </ol>
        </>
      )}
    </section>
  );
}

export default Alabanza;
